import Box from "@mui/material/Box";
import MuiButton from "@mui/material/Button";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ModalBasicLayout, { useModalRequestClose } from "./ModalBasicLayout";
import TikTokCreditBanner from "../TikTok/TikTokCreditBanner";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { getNameBusinessTheme } from "../../actions/getters";
import { GRADIENT_TIKTOK_CREDIT } from "../../utils/colors";
import {
  BUTTON_CONNECT_ACCOUNT,
  BUTTON_CREATE_ACCOUNT,
} from "../../i18n/keysTranslations";

// Lets requestClose pop the history entry without dismissing the flow.
const CREATE_ACCOUNT_CLOSE_REASON = "create-account";

const POPSTATE_SETTLE_TIMEOUT_MS = 300;

// history.back() is async. Opening the next modal before it settles lets that
// modal's pushState interleave with the pending pop and leaves one history
// entry out of sync, so closing it later navigates away from the page.
const waitForHistoryToSettle = () =>
  new Promise((resolve) => {
    const finish = () => {
      window.removeEventListener("popstate", finish);
      clearTimeout(timeoutId);
      resolve();
    };
    const timeoutId = setTimeout(finish, POPSTATE_SETTLE_TIMEOUT_MS);
    window.addEventListener("popstate", finish);
  });

const ConnectOrCreateActions = ({ onConnectAccount, onCreateAccount }) => {
  const requestClose = useModalRequestClose();
  const { t } = useTranslationApp();

  const onConnect = async () => {
    const didClose = await requestClose();
    if (didClose === false) return;
    await onConnectAccount();
  };

  const onCreate = async () => {
    const settled = waitForHistoryToSettle();
    const didClose = await requestClose(CREATE_ACCOUNT_CLOSE_REASON);
    if (didClose === false) return;
    await settled;
    onCreateAccount();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 1,
        px: { xs: 1.75, sm: 2 },
        pb: { xs: 2, sm: 2.5 },
      }}
    >
      <MuiButton
        variant="text"
        onClick={onConnect}
        sx={{
          flex: 1,
          minHeight: 48,
          borderRadius: 999,
          fontWeight: 500,
          textTransform: "none",
          border: "1px solid currentColor",
          color: "rgba(255, 255, 255, 0.72) !important",
          "&:hover": {
            color: "#fff !important",
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        }}
      >
        {t(BUTTON_CONNECT_ACCOUNT)}
      </MuiButton>
      <MuiButton
        variant="contained"
        onClick={onCreate}
        sx={{
          flex: 1,
          minHeight: 48,
          borderRadius: 999,
          fontWeight: 800,
          textTransform: "none",
          backgroundColor: "#fff",
          color: "#0A0A0A !important",
          "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.88)" },
          // The theme sets `.MuiButton-root .MuiSvgIcon-root` to inherit
          // !important, so the arrow needs a more specific selector.
          "&.MuiButton-root .MuiSvgIcon-root": { color: "#fff !important" },
        }}
      >
        {t(BUTTON_CREATE_ACCOUNT)}
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            width: 22,
            height: 22,
            ml: 1,
            borderRadius: "50%",
            backgroundColor: "#0A0A0A",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* The theme forces SvgIcon color with !important. */}
          <ArrowForwardIcon sx={{ fontSize: 14, color: "#fff !important" }} />
        </Box>
      </MuiButton>
    </Box>
  );
};

const ModalTikTokConnectOrCreateAccount = ({
  modalOpen,
  onCloseModal = () => {},
  onConnectAccount = async () => false,
  onCreateAccount = () => {},
}) => {
  const dispatch = useDispatchApp();
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());

  const onRequestClose = (reason) => {
    if (reason === CREATE_ACCOUNT_CLOSE_REASON) return;
    onCloseModal(reason);
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onRequestClose}
      maxWidth="sm"
      fullWidth
      sxPaper={{
        backgroundColor: "#0A0A0A",
        backgroundImage: GRADIENT_TIKTOK_CREDIT,
        overflow: "hidden",
      }}
      // The close button floats over the corner so its row takes no height.
      sxTitle={{
        position: "absolute",
        top: 10,
        right: 10,
        zIndex: 1,
        p: 0,
        backgroundColor: "transparent",
      }}
      sxContent={{ px: 0, pt: 1, pb: 0 }}
      closeButtonSx={{
        color: "#fff !important",
        backgroundColor: "rgba(255, 255, 255, 0.14)",
        // The theme forces SvgIcon color with !important.
        "& .MuiSvgIcon-root": { color: "#fff !important" },
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.24)" },
      }}
    >
      <TikTokCreditBanner nameBusiness={NAME_BUSINESS} fullBleed />
      <ConnectOrCreateActions
        onConnectAccount={onConnectAccount}
        onCreateAccount={onCreateAccount}
      />
    </ModalBasicLayout>
  );
};

export default ModalTikTokConnectOrCreateAccount;
